// This service:
//  * Provides functions for creating/manipulating/inspecting Zones
// Data specs:
//  * polygon = Array<{longitude, latitude}>

import { Cesium } from 'app/cesium';
import { Zone } from 'app/shared/collections/zones/zone';
import { ZoneHistory } from 'app/shared/collections/zones/zone_history';
import { onSiteChangeClosePanel } from 'app/shared/ims_panel_utils';
import { Selection } from 'app/shared/selection/Selection';
import { cartesian3ToDegrees, LatLon } from 'app/shared/utilities/mapping_utils';
import earcut from 'earcut';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import UtilityService from '../utilities/utility.service';
import { closestPastSample, TimeSeriesCollection } from 'time-series-collection';
import { ZoneCollection } from '../collections/zones/zone_collection.service';
import { ZoneHistoryCollection } from '../collections/zones/zone_history_collection.service';
import ZoneTypeCollection from '../collections/zones/zone_type_collection.service';
import { SelectionService } from '../selection/SelectionService';
import { ImsPanelService } from '../ims_panel.service';
import { julianToUnix, toUnix } from '../utilities/time';
import { ClockHandler } from '../time/clock_handler.service';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { CesiumZoneService } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneService';
import { SiteStateService } from '../site_state.service';
import { MapDrawingInputHandler, SaveFunction } from 'app/map/services/map_drawing_input_handler';
import { ZONE_TOUR_HANDLER } from 'app/map/services/map_input_handling/MapInputHandler';
import { MapInputHandlerService } from 'app/map/services/map_input_handling/MapInputHandlerService';

export const NEW_ZONE_KEY = 'new_zone';
const InvalidGeomError = 'invalid_geom';
const SelfIntersectionReason = 'Self-intersection';
const SelfIntersectionErrorFriendlyString = 'Zone shape has invalid self-intersecting lines';

type Triangle = [number, number, number];

interface HistoryPointDetail {
    real: boolean;
    id?: number;
    active: boolean;
    polygon: Array<LatLon>;
    point?: ZoneHistory;
    timestamp: string;
    alreadyExists?: boolean;
}

interface HistoryPointResponseDetail {
    error: string;
    reason: string;
    location: LatLon;
}

export class ZoneService {
    _imsPanelService: ImsPanelService;
    _$timeout;
    _selectionService: SelectionService;
    _zoneTypeCollection: ZoneTypeCollection;
    _zoneCollection: ZoneCollection;
    _zoneHistoryCollection: ZoneHistoryCollection;
    _clockHandler: ClockHandler;
    _mapDrawingService: MapDrawingInputHandler;
    _mapInputHandlerService: MapInputHandlerService;
    _siteStateService: SiteStateService;
    _cesiumZoneService: CesiumZoneService;

    constructor(
        $timeout,
        imsPanelService: ImsPanelService,
        selectionService: SelectionService,
        zoneCollection: ZoneCollection,
        zoneTypeCollection: ZoneTypeCollection,
        zoneHistoryCollection: ZoneHistoryCollection,
        clockHandler: ClockHandler,
        mapDrawingInputHandler: MapDrawingInputHandler,
        siteStateService: SiteStateService,
        mapInputHandlerService: MapInputHandlerService,
        cesiumZoneService: CesiumZoneService,
    ) {
        this._imsPanelService = imsPanelService;
        this._$timeout = $timeout;
        this._selectionService = selectionService;
        this._zoneTypeCollection = zoneTypeCollection;
        this._zoneCollection = zoneCollection;
        this._zoneHistoryCollection = zoneHistoryCollection;
        this._clockHandler = clockHandler;
        this._mapDrawingService = mapDrawingInputHandler;
        this._mapInputHandlerService = mapInputHandlerService;
        this._siteStateService = siteStateService;
        this._cesiumZoneService = cesiumZoneService;
    }

    static get MIN_HISTORY_POINT_SEC_DIFF(): number {
        return 3;
    }

    static get INVALID_NEW_POINT_STR(): string {
        return `Shape history points must be greater than ${ZoneService.MIN_HISTORY_POINT_SEC_DIFF} seconds apart`;
    }

    static polygonToCartesian3s(polygon: LatLon[]): any[] {
        return _.map(polygon, (latLon) => {
            return new Cesium.Cartesian3.fromDegrees(latLon.longitude, latLon.latitude);
        });
    }

    static cartesian3sToPolygon(cartesian3s: { positions: Array<Cesium.Cartesian3> }): LatLon[] {
        return _.map(cartesian3s.positions, (cart3) => cartesian3ToDegrees(cart3));
    }

    static orderedHistoryPoints(zone: Zone): ZoneHistory[] {
        return _.sortBy(_.toArray(zone.historyPoints), 'timestamp');
    }

    static createHistoryIntervals(zone: Zone): void {
        zone.historyIntervals = new TimeSeriesCollection(closestPastSample(Infinity));

        _.forEach(ZoneService.orderedHistoryPoints(zone), (historyPoint) => {
            zone.historyIntervals.addSample(toUnix(new Date(historyPoint.timestamp)), historyPoint);
        });
    }

    static activeHistoryPoint(zone: Zone, julianTime: Cesium.JulianDate): ZoneHistory | null {
        if (!zone || !zone.type) {
            return null;
        }
        if (zone.type.staticShape) {
            return ZoneService.latestHistoryPoint(zone);
        }
        if (!julianTime) {
            return null;
        }
        return zone.historyIntervals.getValue(julianToUnix(julianTime));
    }

    static latestHistoryPoint(zone: Zone): ZoneHistory {
        return zone.historyIntervals.getValue(Number.MAX_SAFE_INTEGER);
    }

    static activePolygonCartesian3s(
        zone: Zone,
        julianTime: Cesium.JulianDate,
    ): Cesium.Cartesian3[] | undefined {
        const historyPoint = ZoneService.activeHistoryPoint(zone, julianTime);
        return historyPoint ? historyPoint.polygonAsCartesian3s : undefined;
    }

    static latestPolygonCartesian3s(zone: Zone): Cesium.Cartesian3[] | undefined {
        const historyPoint = ZoneService.latestHistoryPoint(zone);
        return historyPoint ? historyPoint.polygonAsCartesian3s : undefined;
    }

    static activePosition(zone: Zone, julianTime: Cesium.JulianDate): Cesium.Cartesian3 {
        const hp = ZoneService.activeHistoryPoint(zone, julianTime);
        // https://github.com/AnalyticalGraphicsInc/cesium/blob/7376a742d299e83f055cf5563363f0cbdac469e6/Source/DataSources/LabelVisualizer.js#L125
        // This line means we have to send a clone to the callback, otherwise the last zone position is mutated
        return hp && hp?.polygonCentroidCartesian3 ? hp.polygonCentroidCartesian3.clone() : null;
    }

    static getTrianglesFromZoneHistoryPoint(historyPoint: ZoneHistory): Triangle[] {
        const cart3s = historyPoint.polygonAsCartesian3s;

        if (!cart3s) {
            return undefined;
        }

        const flatCart3s = cart3s.reduce(
            (flatCart3s, cart3) => flatCart3s.concat(cart3.x, cart3.y, cart3.z),
            [],
        );

        return _.chunk<any>(earcut(flatCart3s, null, 3), 3).map((triangle) => [
            cart3s[triangle[0]],
            cart3s[triangle[1]],
            cart3s[triangle[2]],
        ]) as any;
    }

    static reducerForPositionsInZone(
        zone: Zone,
        julianTime: Cesium.JulianDate,
    ): (position: Cesium.Cartesian3) => boolean {
        const historyPoint = ZoneService.activeHistoryPoint(zone, julianTime);
        const triangles = ZoneService.getTrianglesFromZoneHistoryPoint(historyPoint);

        return (position: Cesium.Cartesian3): boolean => {
            return (
                position &&
                triangles.some((triangle) =>
                    Cesium.pointInsideTriangle(position, triangle[0], triangle[1], triangle[2]),
                )
            );
        };
    }

    static convertPolygonToSvgPointsStr(
        polygon: LatLon[],
        containerWidth: number,
        containerHeight: number,
    ): string {
        // convert polygon to x,y points in a square grid
        const points = _.map(polygon, (point: LatLon) => {
            const mercatorN = Math.log(
                Math.tan(Math.PI / 4 + (point.latitude * Math.PI) / 180 / 2),
            );
            return {
                x: (point.longitude + 180) * (containerWidth / 360),
                y: containerHeight / 2 - (containerWidth * mercatorN) / (2 * Math.PI),
            };
        });

        // work out the size of the shape
        const minX1 = Math.min.apply(
            null,
            points.map(({ x, y }) => x),
        );
        const maxX1 = Math.max.apply(
            null,
            points.map(({ x, y }) => x),
        );
        const minY1 = Math.min.apply(
            null,
            points.map(({ x, y }) => y),
        );
        const maxY1 = Math.max.apply(
            null,
            points.map(({ x, y }) => y),
        );
        const shapeWidth = maxX1 - minX1;
        const shapeHeight = maxY1 - minY1;
        const shapeIsWiderThanTall = shapeWidth > shapeHeight;
        const viewSize = shapeIsWiderThanTall ? shapeWidth : shapeHeight;

        // zoom in on the shape, preserving aspect ratio
        const resizedPoints = points.map(({ x, y }) => {
            const xPosInView = x - minX1;
            const yPosInView = y - minY1;
            return {
                x: (xPosInView / viewSize) * containerWidth,
                y: (yPosInView / viewSize) * containerHeight,
            };
        });

        const minX2 = Math.min.apply(
            null,
            resizedPoints.map(({ x, y }) => x),
        );
        const maxX2 = Math.max.apply(
            null,
            resizedPoints.map(({ x, y }) => x),
        );
        const minY2 = Math.min.apply(
            null,
            resizedPoints.map(({ x, y }) => y),
        );
        const maxY2 = Math.max.apply(
            null,
            resizedPoints.map(({ x, y }) => y),
        );

        // center the shape in its new view
        const centeredPoints = resizedPoints.map(({ x, y }) => {
            return {
                x: !shapeIsWiderThanTall ? x + 0.5 * containerWidth - 0.5 * (maxX2 - minX2) : x,
                y: shapeIsWiderThanTall ? y + 0.5 * containerHeight - 0.5 * (maxY2 - minY2) : y,
            };
        });

        return centeredPoints.reduce((str, { x, y }) => ` ${str}${x},${y} `, '');
    }

    static historyPointDetails(
        zone: Zone,
        currentTimeJulian: Cesium.JulianDate,
        editModeOn: boolean,
    ): Array<HistoryPointDetail> {
        const details: Array<HistoryPointDetail> = [];
        const activePoint = ZoneService.activeHistoryPoint(zone, currentTimeJulian);
        const activePointId = _.get(activePoint, 'id');
        const orderedPoints = ZoneService.orderedHistoryPoints(zone);
        const editableHistory = zone.type.editableHistory;

        if (editModeOn && editableHistory && !activePointId) {
            const polygon = _.get(_.first(orderedPoints), 'polygon');
            details.push({
                real: false,
                active: true,
                polygon: polygon ? polygon : [],
                timestamp: currentTimeJulian.toString(),
                alreadyExists: false,
            } as HistoryPointDetail);
        }

        _.forEach(orderedPoints, (point) => {
            const id = _.get(point, 'id');
            const polygon = _.get(point, 'polygon');
            const minJulian = new Cesium.JulianDate.fromIso8601(point.timestamp);
            const maxJulian = new Cesium.JulianDate();
            Cesium.JulianDate.addSeconds(
                minJulian,
                ZoneService.MIN_HISTORY_POINT_SEC_DIFF,
                maxJulian,
            );
            const isLTmax = Cesium.JulianDate.lessThan(currentTimeJulian, maxJulian);
            const active =
                (!editModeOn && activePointId === id) ||
                (editModeOn && activePointId === id && isLTmax);

            details.push({
                real: true,
                id,
                active,
                polygon,
                point: point,
                timestamp: point.timestamp,
            });

            if (editModeOn && activePointId && activePointId === id && editableHistory) {
                details.push({
                    real: false,
                    active: true,
                    polygon,
                    timestamp: currentTimeJulian.toString(),
                    alreadyExists: isLTmax,
                });
            }
        });

        if (editModeOn && !editableHistory) {
            details.push({
                real: false,
                active: true,
                polygon: _.get(_.last(orderedPoints), 'polygon'),
                timestamp: new Date().toISOString(),
            });
        }

        return _.reverse(
            _.sortBy(details, [
                (d) => {
                    return moment(d.timestamp, 'YYYY-MM-DDThh:mm:ssZZ').valueOf();
                },
            ]),
        );
    }

    static mapErrorResponse(
        failureMessage: string,
        responseDetails: HistoryPointResponseDetail,
    ): string {
        if (
            responseDetails.error === InvalidGeomError &&
            responseDetails.reason === SelfIntersectionReason
        ) {
            return `${failureMessage} </br> ${SelfIntersectionErrorFriendlyString}`;
        } else {
            return `${failureMessage} </br> ${responseDetails.error.replace(
                '_',
                ' ',
            )}: ${responseDetails.reason.toLowerCase()}`;
        }
    }

    newZone(name: string, typeId: number, description: string, color: string): Zone {
        const zone = new Zone();
        zone.name = name;
        zone.description = description;
        zone.typeId = typeId;
        zone.color = color;
        zone.type = this._zoneTypeCollection.get(zone.typeId);
        zone.siteId = this._siteStateService.selectedSiteId;
        return zone;
    }

    deleteZone(zone): Promise<any> {
        return this._zoneCollection.removeObject(zone);
    }

    updateZone(zone: Zone, newName: string, newDesc: string, newColor: string): Promise<Zone> {
        // Submit a copy of the zone so that the zone in the collection isn't directly edited
        const zoneCopy = _.cloneDeep(zone);
        zoneCopy.name = newName;
        zoneCopy.description = newDesc ? newDesc : null;
        zoneCopy.color = newColor;
        return this._zoneCollection.updateObject(zoneCopy);
    }

    editHistoryPoint(zone: Zone, historyPoint): void {
        const messages = {
            success: 'Saved shape history point',
            failure: 'Could not save shape history point',
        };

        const saveFn = async (cartesian3s) => {
            const updatedHistoryPoint = historyPoint.clone();
            updatedHistoryPoint.polygon = ZoneService.cartesian3sToPolygon(cartesian3s);
            try {
                await this._zoneHistoryCollection.updateObject(updatedHistoryPoint);
                return {
                    isError: false,
                    details: null,
                };
            } catch (e) {
                if (e?.data?.polygon) {
                    return {
                        isError: true,
                        details: e.data.polygon,
                    };
                }
                throw e;
            }
        };

        this.startEditing(
            zone,
            historyPoint.polygon,
            !zone.type.staticShape,
            saveFn,
            messages,
            false,
        );
    }

    drawNewHistoryPoint(zone, julianDate, copyPolygon): void {
        const messages = {
            success: 'Added new shape history point',
            failure: 'Could not add new shape history point',
        };

        const saveFn = async (cartesian3s) => {
            const historyPoint = new ZoneHistory();
            historyPoint.zoneId = zone.id;
            if (julianDate) {
                historyPoint.timestamp = Cesium.JulianDate.toIso8601(julianDate, 6);
            }
            historyPoint.polygon = ZoneService.cartesian3sToPolygon(cartesian3s);
            try {
                await this._zoneHistoryCollection.addObject(historyPoint);
                return {
                    isError: false,
                    details: null,
                };
            } catch (e) {
                if (e?.data?.polygon) {
                    return {
                        isError: true,
                        details: e.data.polygon,
                    };
                }
                throw e;
            }
        };

        this.startEditing(zone, copyPolygon, !zone.type.staticShape, saveFn, messages, false);
    }

    async convertKMLToZone(name, typeId, description, color, cartesian3s): Promise<void> {
        const zone = this.newZone(name, typeId, description, color);

        const messages = {
            success: `Created ${name}`,
            failure: `Failed to create ${name}`,
        };

        const saveFn = async (cartesian3s) => {
            const initialHistoryPoint = new ZoneHistory();
            initialHistoryPoint.polygon = ZoneService.cartesian3sToPolygon(cartesian3s);

            // the api pulls initialHistoryPoint off of the zone and saves it,
            // so we need to provide the underlying resource here, rather than the imsObject
            zone.initialHistoryPoint = initialHistoryPoint.resource;

            try {
                await this._zoneCollection.addObject(zone);
            } catch (e) {
                if (e?.data?.polygon) {
                    return {
                        isError: true,
                        details: e.data.polygon,
                    };
                }
                throw e;
            }

            // 'reselect' with the new imsObject from the api
            this._zoneCollection.temporaryUnsavedZone = undefined;
            await this._selectionService.setSelection(new Selection(zone.id, SelectionType.ZONE));
            // collect the initial history point (and persist it) so it renders straight away
            await this._zoneHistoryCollection.collectLatestImsObj(
                zone.initialHistoryPoint.id,
                true,
            );
            return {
                isError: false,
                details: null,
            };
        };
        this._zoneCollection.temporaryUnsavedZone = zone;

        // Need to wait for this async operation to complete before editing the shape starts.
        await this._selectionService.setSelection(new Selection(NEW_ZONE_KEY, SelectionType.ZONE));

        this.startEditing(
            zone,
            ZoneService.cartesian3sToPolygon(cartesian3s),
            !zone.type.editableHistory,
            saveFn,
            messages,
            true,
        );
    }

    async drawNewZone(
        name: string,
        typeId: number,
        description: string,
        color: string,
    ): Promise<void> {
        const zone = this.newZone(name, typeId, description, color);

        const messages = {
            success: `Created ${name}`,
            failure: `Failed to create ${name}`,
        };

        const saveFn = async (cartesian3s) => {
            const initialHistoryPoint = new ZoneHistory();

            const julian = this._clockHandler.getCursorTimeAsJulian();
            initialHistoryPoint.timestamp = Cesium.JulianDate.toIso8601(julian, 6);

            initialHistoryPoint.polygon = ZoneService.cartesian3sToPolygon(cartesian3s);

            // the api pulls initialHistoryPoint off of the zone and saves it,
            // so we need to provide the underlying resource here, rather than the imsObject
            zone.initialHistoryPoint = initialHistoryPoint.resource;

            try {
                await this._zoneCollection.addObject(zone);
            } catch (e) {
                if (e?.data?.polygon) {
                    return {
                        isError: true,
                        details: e.data.polygon,
                    };
                }
                throw e;
            }

            // 'reselect' with the new imsObject from the api
            this._zoneCollection.temporaryUnsavedZone = undefined;
            await this._selectionService.setSelection(new Selection(zone.id, SelectionType.ZONE));
            // collect the initial history point (and persist it) so it renders straight away
            await this._zoneHistoryCollection.collectLatestImsObj(
                zone.initialHistoryPoint.id,
                true,
            );
            return {
                isError: false,
                details: null,
            };
        };

        this._zoneCollection.temporaryUnsavedZone = zone;

        // Need to wait for this async operation to complete before editing the shape starts.
        await this._selectionService.setSelection(new Selection(NEW_ZONE_KEY, SelectionType.ZONE));

        this.startEditing(zone, null, !zone.type.editableHistory, saveFn, messages, true);
    }

    startEditing(
        zone: Zone,
        existingPolygon: Array<LatLon>,
        lockTimeline: boolean,
        saveFn: (ShapeToSave) => Promise<{
            isError: boolean;
            details: HistoryPointResponseDetail;
        }>,
        messages: { success: string; failure: string },
        closeInfoboxIfCancelled: boolean,
    ): void {
        const cartesian3s = existingPolygon
            ? ZoneService.polygonToCartesian3s(existingPolygon)
            : null;

        const wrappedSaveFn: SaveFunction = async (cartesian3s) => {
            if (cartesian3s) {
                try {
                    const response = await saveFn(cartesian3s);
                    if (response.isError) {
                        return {
                            success: false,
                            msg: ZoneService.mapErrorResponse(messages.failure, response.details),
                            invalidPolygonDetail: response.details,
                        };
                    } else {
                        return {
                            success: true,
                            msg: messages.success,
                            invalidPolygonDetail: null,
                        };
                    }
                } catch (error) {
                    return {
                        success: false,
                        msg: messages.failure + UtilityService.apiErrorIntoText(error),
                        invalidPolygonDetail: null,
                    };
                }
            } else {
                return Promise.resolve({
                    success: false,
                    msg: messages.failure,
                    invalidPolygonDetail: null,
                });
            }
        };

        zone.editingPoints = true;
        this._zoneCollection.collectionChanged({}, this._zoneCollection.constructor.name);

        this._mapDrawingService
            .startDrawing(cartesian3s, lockTimeline, wrappedSaveFn)
            .then(async (wasCancelled) => {
                zone.editingPoints = false;
                this._zoneCollection.collectionChanged({}, this._zoneCollection.constructor.name);

                if (wasCancelled && closeInfoboxIfCancelled) {
                    await this._selectionService.clearSelection();
                } else {
                    // Refresh zones on map to represent new editingPoints state
                    this._cesiumZoneService.redrawAllZonesOnMap();
                }
            });
    }

    public async launchNewZoneWizard(): Promise<void> {
        await this._mapInputHandlerService.setActiveHandlerByName(ZONE_TOUR_HANDLER);

        const tag = 'zoneWizardPanel';
        if (!this._imsPanelService.getPanelApiByTag(tag)) {
            const api = this._imsPanelService.newPanel('detached', {
                tag,
                headerText: 'Create New Zone',
                darkTheme: true,
                detachedWidth: 500,
                persistent: false,
                innerComponent: 'zoneWizard',
                resizable: false,
            });
            onSiteChangeClosePanel(api, this._siteStateService);
        }
    }

    moveTimelineToHistoryPoint(historyPoint): void {
        // We add 0.1 seconds here to avoid time being rounded down and zones getting the wrong history point
        this._clockHandler.moveCursorToMoment(
            moment(historyPoint.timestamp).add(0.1, 'seconds'),
            true,
        );
    }
}
